import { withAuthenticationRequired } from '@auth0/auth0-react';
import React, { useMemo } from 'react';
import { Route, RouteProps } from 'react-router-dom';

import Loading from '../components/Loading';
import { AuthContext, useAuth } from './AuthProvider';

// Inspired by https://auth0.com/blog/complete-guide-to-react-user-authentication/
// withAuthenticationRequired is memoized on `component` identity so that
// repeated renders of <ProtectedRoute /> while unauthenticated do NOT
// produce a fresh wrapper component each render (which would cause the
// underlying Route element to remount the protected child on every render).

const ProtectedRoute: React.FC<RouteProps> = ({ component, ...args }) => {
  const auth = useAuth() as AuthContext | undefined;
  const isAuthAuthenticated = auth?.isAuthenticated ?? false;

  const wrappedComponent = useMemo(
    () =>
      !isAuthAuthenticated && component
        ? withAuthenticationRequired(component, {
            onRedirecting: () => <Loading />
          })
        : undefined,
    [component, isAuthAuthenticated]
  );

  if (!component) {
    throw new Error('Unreachable: component of ProtectedRoute is not set');
  }

  return (
    <Route
      component={isAuthAuthenticated ? component : wrappedComponent}
      {...args}
    />
  );
};

export default ProtectedRoute;
